import { useCallback, useEffect, useState } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import { NumericFormat } from "react-number-format";
import TextField from "@mui/material/TextField";
import TransactionType from "@homegame/common/dist/enum/transaction-type.enum";
import FormControl from "@mui/material/FormControl";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import UserInterface from "@homegame/common/dist/interface/user.interface";
import GameInterface from "@homegame/common/dist/interface/game.interface";
import TransactionService from "../service/transaction.service";
import UserService from "../service/user.service";
import { useAuth } from "../context/auth.context";
import SelectUserComponent from "./select-user.component";
import { Controller, useForm } from "react-hook-form";
import TransactionStatus from "@homegame/common/dist/enum/transaction-status.enum";
import InputAdornment from "@mui/material/InputAdornment";
import useGameInfo from "../hooks/useGameInfo/use-game-info.hook";
import LoadingButton from "./loading-button.component";
import { useToast } from "../hooks/use-toast.hook";
import Typography from "@mui/material/Typography";
import formatCurrency from "../helpers/format-currency.helper";
import TransactionInterface from "@homegame/common/dist/interface/transaction.interface";
import AssignDebtComponent from "./assign-debt.component";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import UserDebtsService from "../service/user-debts.service";
import computeDebtToTable from "../helpers/compute-debt-to-table.helper";
import { CashoutDebtItem } from "../../transactions/components/transaction-item.component";

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      color: "#fff",
      fontWeight: 300,
      textAlign: "center",
    },
    form: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      padding: "10px 27px 15px 27px",
    },
    btn: { width: 200, marginTop: 15 },
    btnAccept: { width: 200, marginTop: 15, backgroundColor: "green" },
    btnAssign: { width: 200, margin: "0 auto", marginTop: 15 },
    moneyInput: {
      color: "#fff",
      "& .MuiTypography-root, & .Mui-disabled": {
        color: "#fff !important",
        "-webkit-text-fill-color": "#fff",
      },
    },
    errorText: {
      backgroundColor: "red",
      color: "#fff !important",
      fontSize: 12,
      textShadow: "none",
      padding: "5px 15px",
      borderRadius: 5,
      marginLeft: 0,
    },
    valueText: {
      color: "#ddd",
      fontSize: 12,
    },
  }),
);

export default function CreateCashoutDialog({
  onClose,
  game,
  users,
  toUser,
  open = true,
  fromUser,
  amount = 0,
  initialValue,
}: {
  users?: UserInterface[];
  onClose?: () => any;
  game?: GameInterface;
  toUser?: UserInterface;
  fromUser?: UserInterface;
  open?: boolean;
  amount?: number;
  initialValue?: TransactionInterface;
}) {
  const classes: any = useStyles({});
  const transactionService = new TransactionService();
  const userService = new UserService();
  const debtsService = new UserDebtsService();
  const { showToast } = useToast();
  const { user } = useAuth();
  const [usersList, setUsersList] = useState<UserInterface[]>(users || []);
  const [fullTransaction, setFullTransaction] =
    useState<TransactionInterface | null>(initialValue ?? null);
  const [debtToTable, setDebtToTable] = useState<number>(0);

  const [debtsAssigned, setDebtsAssigned] = useState<
    Partial<TransactionInterface>[]
  >([]);

  const [currentStep, setCurrentStep] = useState<number>(
    initialValue?.status === TransactionStatus.PENDING ? 2 : 0,
  );

  const [debtDialogVisible, setDebtDialogVisible] = useState<boolean>(false);

  const [debtToEdit, setDebtToEdit] = useState<
    { debt: Partial<TransactionInterface>; index: number } | undefined
  >(undefined);

  const [isTransactionCreating, setIsTransactionCreating] =
    useState<boolean>(false);

  const { currentChipsVal, currentMoneyVal, gameRate, gameDebts } =
    useGameInfo(game);

  const totalGameChips = currentChipsVal;

  let defaultChipsAmount = "";

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: initialValue
      ? {
          ...initialValue,
          fromUser,
          chips: String(initialValue.chips),
          amount: String(initialValue.amount),
        }
      : {
          fromUser: fromUser,
          toUser: toUser,
          chips: defaultChipsAmount,
          amount:
            amount > 0
              ? String(amount)
              : game
                ? String(
                    Number(defaultChipsAmount) *
                      (game.moneyValue / game.chipValue),
                  )
                : defaultChipsAmount,
          tableId: game?.tableId,
          gameId: game?.id,
          status: TransactionStatus.PENDING,
          debts: [],
        },
  });

  useEffect(() => {
    const loadTransaction = async () => {
      if (!initialValue?.id) return;

      try {
        const fullTx = await transactionService.getById(initialValue.id);
        setFullTransaction(fullTx);
        reset({
          ...fullTx,
          chips: String(fullTx.chips),
          amount: String(fullTx.amount),
        });

        if (fullTx.children && fullTx.children.length) {
          setDebtsAssigned(fullTx.children);
        }
      } catch (e) {
        console.error("Failed to load transaction", e);
        showToast("Could not load transaction data", "error");
      }
    };

    loadTransaction();
  }, [initialValue?.id]);

  const onAssignDebtClose = () => {
    setDebtToEdit(undefined);
    setDebtDialogVisible(false);
  };

  const showAssignDebtDialog = async () => {
    setDebtDialogVisible(true);
  };

  const chipsValue = watch("chips");

  const loadUsers = useCallback(async () => {
    try {
      const [items] = await userService.list({
        forTransaction: true,
        excludeSelf: true,
      });
      setUsersList(items);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (!game) loadUsers().then();
  }, []);

  useEffect(() => {
    if (chipsValue === "0") {
      setValue("amount", "0");
      trigger("amount");
      setDebtToTable(
        computeDebtToTable(
          game?.transactions || [],
          fromUser!.id!,
          Number(chipsValue),
          gameRate,
        ),
      );
    }
  }, [chipsValue]);

  const onSubmit = async (data: any) => {
    if (!game) return;
    try {
      if (data.chips < 0) {
        showToast("Chips amount must be greater than 0!", "warning");
        return;
      }

      if (data.chips > totalGameChips) {
        showToast("You cannot return more chips than total in game", "warning");
        return;
      }

      setIsTransactionCreating(true);
      const savedTransaction = await transactionService.save({
        ...data,
        amount: data.amount || 0,
        fromUser: data.toUser,
        toUser: data.fromUser,
        toUserId: data.fromUser!.id,
        fromUserId: data.toUser!.id,
        type: TransactionType.CASH_OUT,
        status:
          debtsAssigned.length > 0
            ? TransactionStatus.UNASSIGNED
            : TransactionStatus.PENDING,
      });

      if (debtsAssigned.length > 0) {
        for (const debt of debtsAssigned) {
          await debtsService.assignDebt({
            ...debt,
            fromUserId: data.fromUser!.id,
            toUserId: debt.toUser!.id,
            initialTransactionId: savedTransaction.id,
          });
        }
      }
      if (onClose) onClose();
      const successMessage = "Cash out added!";
      showToast(successMessage, "success");
    } catch (e: any) {
      const { message } = e as Error;
      showToast(message, "error");
    } finally {
      setIsTransactionCreating(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  if (!user || !game) return <p>Error</p>;

  const getTransactionFrom = () => {
    return game.table?.user;
  };

  /**
   * Validates the value of chips for a transaction.
   *
   * @param {string} value - The value of chips to validate.
   * @returns {boolean | string} Returns true if the value is valid. Otherwise, returns an error message.
   */
  const chipsValueValidate = (value: string): boolean | string => {
    // Return true if the game data is not defined
    if (!game) return true;

    // Check if the value is a number
    const parsedValue = parseInt(value);
    if (isNaN(parsedValue)) return "Wrong value";

    if (parsedValue > totalGameChips)
      return "You cannot return more chips than total in game";

    return true;
  };

  const moneyValueValidate = (value: string) => {
    if (!game) return true;
    if (isNaN(parseInt(value))) return "Wrong value";
    const parsedValue = parseInt(value);
    const maxByChips = Number(chipsValue) * gameRate;

    const userDebts = gameDebts.filter(
      (d) =>
        d.toUserId === fromUser?.id &&
        [
          TransactionStatus.PENDING,
          TransactionStatus.APPROVED,
          TransactionStatus.OPEN,
        ].includes(d.status),
    );

    const totalDebt = userDebts.reduce((sum, d) => sum + (d.amount || 0), 0);

    const availableAfterDebt = maxByChips - totalDebt;

    if (parsedValue > Number(chipsValue) * gameRate) {
      return `The money amount cannot exceed your total chips multiplied by the game rate (${formatCurrency(
        Number(chipsValue) * gameRate,
        game.currency,
      )}).`;
    }

    if (parsedValue > currentMoneyVal) {
      return `Cannot cash out more than money left in game (${currentMoneyVal} ${game.currency})`;
    }

    if (parsedValue > availableAfterDebt) {
      return `You have ${formatCurrency(totalDebt, game.currency)} in debts. You can only receive up to ${formatCurrency(
        availableAfterDebt,
        game.currency,
      )} from cashout.`;
    }

    return true;
  };

  const getChipsFieldLabel = () => {
    const chipsRate = game.moneyValue / game.chipValue;
    const amount = Number(chipsValue) * chipsRate;
    const formattedAmount = formatCurrency(amount, game.currency);
    return `Chips player returns (value ${formattedAmount})`;
  };

  const getMoneyFieldLabel = () => {
    return `Money player received`;
  };

  const handleDeleteDebt = (index: number) => {
    setDebtsAssigned((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditDebt = (index: number) => {
    const selected = debtsAssigned[index];
    setDebtToEdit({ debt: selected, index });
    setDebtDialogVisible(true);
  };

  const handleNextStep = () => {
    if (Number(chipsValue) > 0 && currentStep === 0) {
      setCurrentStep(2);
      return;
    }
    setCurrentStep((prev) => {
      return prev + 1;
    });
  };

  const isApproveDisabled =
    (debtsAssigned.length > 1 &&
      debtsAssigned.reduce((acc, d) => acc + Number(d.amount), 0) <
        Math.abs(debtToTable)) ||
    debtsAssigned.some((d) => d.status === TransactionStatus.PENDING);

  const isAssignDebtAllowed =
    debtsAssigned
      .filter(
        (d) =>
          d.status === TransactionStatus.NOT_SENT ||
          d.status === TransactionStatus.PENDING ||
          d.status === TransactionStatus.APPROVED ||
          d.status === TransactionStatus.OPEN,
      )
      .reduce((acc, d) => acc + Number(d.amount), 0) < Math.abs(debtToTable);

  const getValidAssignedAmount = (
    debts: Partial<TransactionInterface>[],
    validStatuses: TransactionStatus[] = [
      TransactionStatus.PENDING,
      TransactionStatus.APPROVED,
      TransactionStatus.OPEN,
    ],
  ): number => {
    return debts
      .filter((d) =>
        validStatuses.includes(d.status ?? TransactionStatus.PENDING),
      )
      .reduce((acc, d) => acc + Number(d.amount), 0);
  };
  const totalAssigned = getValidAssignedAmount(debtsAssigned);
  const previousAmount = debtToEdit?.debt?.amount || 0;
  const maxAmount = -debtToTable + previousAmount - totalAssigned;

  const approveCashout = async () => {
    try {
      await transactionService.approve(initialValue!.id as number);
    } catch (e) {
      showToast((e as Error).message, "error");
    }
  };

  return (
    <Dialog
      fullWidth
      onClose={handleClose}
      aria-labelledby="invite-dialog-title"
      open={open}
    >
      <DialogTitle id="invite-dialog-title" className={classes.title}>
        Cash out
      </DialogTitle>

      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        {currentStep === 0 && (
          <>
            <FormControl
              fullWidth
              style={{ display: !!fromUser ? "none" : "inline-block" }}
            >
              <Controller
                render={({ field }) => (
                  <SelectUserComponent
                    label="From player"
                    {...field}
                    disabled={!!fromUser || !game}
                    list={game ? usersList : [user]}
                  />
                )}
                name="fromUser"
                control={control}
                rules={{ required: "Field required" }}
              />
              {errors.fromUser && (
                <FormHelperText className={classes.errorText} error>
                  {errors.fromUser.message}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl fullWidth style={{ display: "inline-block" }}>
              <Controller
                render={({ field }) => (
                  <NumericFormat
                    {...field}
                    customInput={TextField}
                    fullWidth
                    label={getChipsFieldLabel()}
                    allowNegative={false}
                    fixedDecimalScale={true}
                    variant="filled"
                    autoFocus={true}
                  />
                )}
                name="chips"
                control={control}
                rules={{
                  required: "Field required",
                  validate: chipsValueValidate,
                }}
              />

              {errors.chips && (
                <FormHelperText className={classes.errorText} error>
                  {errors.chips.message}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth className={classes.moneyInput}>
              <Controller
                render={({ field }) => (
                  <NumericFormat
                    {...field}
                    customInput={TextField}
                    fullWidth
                    label={getMoneyFieldLabel()}
                    allowNegative={true}
                    allowLeadingZeros={true}
                    fixedDecimalScale={true}
                    variant="filled"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          {formatCurrency(0, game?.currency, false)}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                name="amount"
                disabled={!chipsValue || chipsValue === "0"}
                control={control}
                rules={{
                  required: "Field required",
                  validate: moneyValueValidate,
                }}
              />

              {errors.amount && (
                <FormHelperText className={classes.errorText} error>
                  {errors.amount.message}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl
              fullWidth
              style={{ display: !!toUser ? "none" : "inline-block" }}
            >
              <Controller
                render={({ field }) => (
                  <SelectUserComponent
                    label="To player"
                    {...field}
                    disabled={!!toUser}
                    list={usersList}
                  />
                )}
                name="toUser"
                control={control}
                rules={{ required: "Field required" }}
              />
              {/*// @ts-ignore*/}
              {errors.toUser && (
                <FormHelperText className={classes.errorText} error>
                  {errors.toUser.message}
                </FormHelperText>
              )}
            </FormControl>

            <LoadingButton
              text={"Continue"}
              variant="contained"
              disabled={!isValid}
              className={classes.btnAccept}
              isLoading={isTransactionCreating}
              onClick={handleNextStep}
            />

            <Button
              className={classes.btn}
              onClick={handleClose}
              variant="contained"
              color="secondary"
            >
              Close
            </Button>
          </>
        )}
        {currentStep === 1 && (
          <>
            {debtToTable < 0 && (
              <>
                <FormControl fullWidth className={classes.moneyInput}>
                  <NumericFormat
                    value={Math.abs(debtToTable)}
                    disabled
                    customInput={TextField}
                    fullWidth
                    label="Debt to table"
                    allowNegative={true}
                    allowLeadingZeros={true}
                    fixedDecimalScale={true}
                    variant="filled"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          {formatCurrency(0, game?.currency, false)}
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    variant="contained"
                    color="secondary"
                    className={classes.btnAssign}
                    startIcon={<ShoppingCartCheckoutIcon />}
                    onClick={showAssignDebtDialog}
                  >
                    Assign Debt
                  </Button>

                  {debtsAssigned.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <Typography variant="subtitle2">
                        Assigned Debts:
                      </Typography>
                      {debtsAssigned.map((d, i) => (
                        <CashoutDebtItem
                          transaction={d}
                          onDeleteClick={() => handleDeleteDebt(i)}
                          onEditClick={() => handleEditDebt(i)}
                        />
                      ))}
                    </div>
                  )}
                </FormControl>
              </>
            )}

            <LoadingButton
              text={"Continue"}
              variant="contained"
              className={classes.btnAccept}
              isLoading={isTransactionCreating}
              onClick={handleNextStep}
              disabled={
                debtsAssigned.reduce((acc, d) => acc + Number(d.amount), 0) !==
                Math.abs(debtToTable)
              }
            />
          </>
        )}
        {currentStep === 2 && (
          <>
            <Typography
              variant="subtitle2"
              style={{ color: "#ccc", marginBottom: 12 }}
            >
              {initialValue?.status === TransactionStatus.PENDING
                ? "Pending cashout"
                : "Review and confirm"}
            </Typography>

            <FormControl fullWidth className={classes.moneyInput}>
              <TextField
                value={watch("chips")}
                disabled
                label={getChipsFieldLabel()}
                variant="filled"
                fullWidth
              />
            </FormControl>

            <FormControl fullWidth className={classes.moneyInput}>
              <TextField
                value={watch("amount")}
                disabled
                label={getMoneyFieldLabel()}
                variant="filled"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      {formatCurrency(0, game?.currency, false)}
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            {chipsValue === "0" && debtsAssigned.length > 0 && (
              <div style={{ marginTop: 16, width: "100%" }}>
                <Typography variant="subtitle2" style={{ color: "#ccc" }}>
                  Assigned Debts:
                </Typography>
                {debtsAssigned.map((d, i) => (
                  <CashoutDebtItem
                    transaction={d}
                    showControls={false}
                    onDeleteClick={() => handleDeleteDebt(i)}
                    onEditClick={() => handleEditDebt(i)}
                    relatedTransactions={fullTransaction?.children}
                  />
                ))}
              </div>
            )}

            {isAssignDebtAllowed && (
              <Button
                variant="contained"
                color="secondary"
                className={classes.btnAssign}
                startIcon={<ShoppingCartCheckoutIcon />}
                onClick={showAssignDebtDialog}
              >
                Assign Debt
              </Button>
            )}

            {initialValue?.id && (
              <LoadingButton
                text="Approve cashout"
                type="button"
                onClick={approveCashout}
                variant="contained"
                disabled={isApproveDisabled}
                className={classes.btnAccept}
                isLoading={isTransactionCreating}
              />
            )}

            {!initialValue?.id && (
              <LoadingButton
                text="Save"
                type="submit"
                variant="contained"
                disabled={isApproveDisabled}
                className={classes.btnAccept}
                isLoading={isTransactionCreating}
              />
            )}

            <Button
              className={classes.btn}
              onClick={handleClose}
              variant="contained"
              color="secondary"
            >
              Close
            </Button>
          </>
        )}
      </form>

      <AssignDebtComponent
        open={debtDialogVisible}
        game={game}
        cashoutFromUser={fromUser}
        onClose={onAssignDebtClose}
        fromUser={getTransactionFrom()}
        maxAmount={maxAmount}
        initialValue={debtToEdit?.debt || {}}
        onAssign={async (newDebt) => {
          const updatedDebts = [...debtsAssigned];

          if (debtToEdit) {
            updatedDebts[debtToEdit.index] = newDebt;
            setDebtToEdit(undefined);
          } else {
            updatedDebts.push(newDebt);
          }

          setDebtsAssigned(updatedDebts);

          if (initialValue?.id) {
            try {
              await debtsService.assignDebt({
                ...newDebt,
                fromUserId: fromUser!.id,
                toUserId: toUser?.id,
                initialTransactionId: initialValue.id,
                gameId: game?.id,
                tableId: game?.tableId,
              });

              showToast("Debt assigned", "success");
            } catch (e) {
              console.error("Failed to assign debt", e);
              showToast("Could not assign debt", "error");
            }
          }
        }}
      />
    </Dialog>
  );
}
